/**
 * Workflow API Service
 *
 * Typed client for all workflow-related endpoints:
 * - Workflow CRUD
 * - Workflow run trigger, list, detail, cancel
 */

import { apiClient } from './api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WorkflowScope = 'org' | 'team' | 'user';
export type WorkflowStatus = 'active' | 'disabled' | 'draft';
export type WorkflowRunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type TriggerSource = 'manual' | 'scheduled' | 'api' | 'chat';
export type StepType = 'agent_call' | 'skill_apply' | 'condition' | 'merge';
export type OnFailureAction = 'abort' | 'continue';

/** Step as edited in the UI (flat, dependencies inline). Persisted through toApiDefinition. */
export interface WorkflowStepNode {
  id: string;
  name: string;
  type: StepType;
  agent_id?: string;
  skill_id?: string;
  condition_expr?: string;
  depends_on: string[];
  input_template?: string;
  on_failure: OnFailureAction;
  timeout_ms: number;
}

/** Per-node config as the API stores it (passthrough object: extra keys such as name are kept). */
export interface ApiWorkflowNodeConfig {
  name?: string;
  input_template?: string | Record<string, unknown>;
  timeout_ms?: number;
  retry_count?: number;
  on_failure?: OnFailureAction;
  [key: string]: unknown;
}

/**
 * Node exactly as the API returns it. The API keeps id, type, agent_id, skill_id,
 * condition_expr and config; dependencies live in definition.edges. name and depends_on are
 * only present on legacy definitions, so every field but id and type is optional.
 */
export interface ApiWorkflowNode {
  id: string;
  type: StepType;
  name?: string;
  agent_id?: string;
  skill_id?: string;
  condition_expr?: string;
  depends_on?: string[];
  config?: ApiWorkflowNodeConfig;
}

export interface WorkflowDefinition {
  nodes: ApiWorkflowNode[];
  edges: Array<{ from: string; to: string; condition?: string }>;
}

export interface Workflow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  definition: WorkflowDefinition;
  scope: WorkflowScope;
  status: WorkflowStatus;
  version: number;
  is_activated_by_current_user: boolean;
  /** Total number of runs executed for this workflow */
  run_count: number;
  /** Status of the most recent run (null if never run) */
  last_run_status: WorkflowRunStatus | null;
  created_at: string;
  updated_at: string;
}

export type StepLogStatus =
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'skipped';

/** Step log as the API returns it: keyed by node_id, with no step name. */
export interface StepLog {
  node_id: string;
  status: StepLogStatus;
  started_at: string | null;
  finished_at: string | null;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: string | null;
  tokens: number;
  cost_usd: number | string;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string;
  trigger_source: TriggerSource;
  status: WorkflowRunStatus;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  started_at: string | null;
  finished_at: string | null;
  total_tokens: number;
  total_cost_usd: string;
  step_logs: StepLog[];
  error_message: string | null;
  created_at: string;
  triggered_by_name?: string | null;
  /** False when the API blanked input, output and errors for who did not trigger the run */
  content_visible?: boolean;
}

export interface CreateWorkflowInput {
  name: string;
  slug: string;
  description?: string;
  definition: WorkflowDefinition;
  scope: WorkflowScope;
  owner_team_id?: string;
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  definition?: WorkflowDefinition;
  status?: WorkflowStatus;
}

export interface ListWorkflowsParams {
  scope?: WorkflowScope | 'all';
  page?: number;
  per_page?: number;
}

export interface ListWorkflowsResponse {
  data: Workflow[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ListWorkflowRunsParams {
  cursor?: string;
  limit?: number;
  status?: WorkflowRunStatus;
}

export interface ListWorkflowRunsResponse {
  data: WorkflowRun[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface RunWorkflowResponse {
  workflow_run_id: string;
  status: 'queued';
}

// ---------------------------------------------------------------------------
// Agent/Skill list types (for dropdowns)
// ---------------------------------------------------------------------------

export interface AgentSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface SkillSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

const WORKFLOWS_PATH = '/v1/workflows';
const WORKFLOW_RUNS_PATH = '/v1/workflow-runs';

export const workflowApi = {
  /** List workflows */
  async list(params?: ListWorkflowsParams): Promise<ListWorkflowsResponse> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.page) query.set('page', String(params.page));
    if (params?.per_page) query.set('per_page', String(params.per_page));
    const qs = query.toString();
    return apiClient.get<ListWorkflowsResponse>(`${WORKFLOWS_PATH}${qs ? `?${qs}` : ''}`);
  },

  /** Get workflow by ID */
  async get(id: string): Promise<Workflow> {
    return apiClient.get<Workflow>(`${WORKFLOWS_PATH}/${id}`);
  },

  /** Create workflow */
  async create(input: CreateWorkflowInput): Promise<Workflow> {
    return apiClient.post<Workflow>(WORKFLOWS_PATH, input);
  },

  /** Update workflow */
  async update(id: string, input: UpdateWorkflowInput): Promise<Workflow> {
    return apiClient.patch<Workflow>(`${WORKFLOWS_PATH}/${id}`, input);
  },

  /** Soft-delete workflow */
  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`${WORKFLOWS_PATH}/${id}`);
  },

  /** Activate workflow for current user */
  async activate(id: string): Promise<void> {
    return apiClient.post<void>(`${WORKFLOWS_PATH}/${id}/activate`);
  },

  /** Deactivate workflow for current user */
  async deactivate(id: string): Promise<void> {
    return apiClient.post<void>(`${WORKFLOWS_PATH}/${id}/deactivate`);
  },

  /** Duplicate workflow */
  async duplicate(id: string, input?: { new_name?: string; new_slug?: string; scope?: WorkflowScope }): Promise<Workflow> {
    return apiClient.post<Workflow>(`${WORKFLOWS_PATH}/${id}/duplicate`, input ?? {});
  },

  /** Trigger workflow execution */
  async run(id: string, input?: Record<string, unknown>): Promise<RunWorkflowResponse> {
    return apiClient.post<RunWorkflowResponse>(`${WORKFLOWS_PATH}/${id}/run`, { input });
  },

  /** List runs for a workflow */
  async listRuns(workflowId: string, params?: ListWorkflowRunsParams): Promise<ListWorkflowRunsResponse> {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    const qs = query.toString();
    return apiClient.get<ListWorkflowRunsResponse>(
      `${WORKFLOWS_PATH}/${workflowId}/runs${qs ? `?${qs}` : ''}`,
    );
  },

  /** Get workflow run detail with step logs */
  async getRun(runId: string): Promise<WorkflowRun> {
    return apiClient.get<WorkflowRun>(`${WORKFLOW_RUNS_PATH}/${runId}`);
  },

  /** Cancel a running workflow */
  async cancelRun(runId: string): Promise<WorkflowRun> {
    return apiClient.post<WorkflowRun>(`${WORKFLOW_RUNS_PATH}/${runId}/cancel`);
  },

  /** List agents for step dropdown */
  async listAgents(): Promise<{ data: AgentSummary[] }> {
    return apiClient.get<{ data: AgentSummary[] }>('/v1/agents?scope=all&status=active');
  },

  /** List skills for step dropdown */
  async listSkills(): Promise<{ data: SkillSummary[] }> {
    return apiClient.get<{ data: SkillSummary[] }>('/v1/skills?scope=all&status=active');
  },
};
